import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import BookForm, { BookFormSubmission } from './components/BookForm';
import BookHistory from './components/BookHistory';
import BookList from './components/BookList';
import BookNotes from './components/BookNotes';
import BookNotesPreview from './components/BookNotesPreview';
import HistoryPage from './components/HistoryPage';
import Login from './authComponents/Login';
import Signup from './authComponents/Signup';
import PasswordReset from './authComponents/PasswordReset';
import Terms from './authComponents/Terms';
import { Book, BookStatus, STANDARD_ITEM_TYPES } from './objects/Book';
import { createBook, createNote, listBooks, updateBook, updateQueue } from './repository/readingRepository';
import { clearAutoRefresh } from './utils/tokenRefresh';

const sortQueue = (books: Book[]): Book[] => {
  return [...books].sort((a, b) => (a.queue_position || 0) - (b.queue_position || 0));
};

const escapeMarkdown = (value: string): string => value.replace(/_/g, '\\_');

const formatBookMarkdown = (book: Book): string => {
  const title = escapeMarkdown(book.title.trim());
  const author = book.author?.trim();
  const authorPart = author ? `, ${escapeMarkdown(author)}` : '';
  return `_${title}_${authorPart}`;
};

const AppShell: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markdownCopied, setMarkdownCopied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listBooks();
      setBooks(data);
    } catch (err) {
      setError('Unable to load books.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const queueBooks = useMemo(
    () => sortQueue(books.filter((book) => book.status === 'in queue')),
    [books]
  );
  const readingBooks = useMemo(
    () => books.filter((book) => book.status === 'reading'),
    [books]
  );
  const occasionalReferenceBooks = useMemo(
    () => books.filter((book) => book.status === 'occasional reference'),
    [books]
  );
  const finishedBooks = useMemo(
    () => books.filter((book) => book.status === 'done'),
    [books]
  );
  const abandonedBooks = useMemo(
    () => books.filter((book) => book.status === 'abandoned'),
    [books]
  );
  const probablyNotBooks = useMemo(
    () => books.filter((book) => book.status === 'probably not'),
    [books]
  );
  const getBookById = useCallback(
    (bookId?: string) => books.find((book) => book.uuid === bookId),
    [books]
  );

  const handleCreate = async (data: BookFormSubmission) => {
    try {
      let parentUuid = data.parentUuid;
      if (data.isSection && data.newParent) {
        const createdParent = await createBook({
          title: data.newParent.title,
          author: data.newParent.author,
          status: 'reading',
          item_type: data.newParent.itemType,
        });
        parentUuid = createdParent.uuid;
        setBooks((prev) => [...prev, createdParent]);
      }

      const newBook = await createBook({
        title: data.title,
        author: data.author,
        status: data.status,
        item_type: data.itemType,
        is_section: data.isSection || undefined,
        parent_uuid: parentUuid,
        section_description: data.sectionDescription,
      });
      setBooks((prev) => [...prev, newBook]);

      if (data.note) {
        try {
          await createNote(newBook.uuid, data.note);
        } catch {
          setError('Item added, but the note could not be saved.');
        }
      }
    } catch (err) {
      setError('Unable to add item.');
    }
  };

  const handleStatusChange = async (book: Book, status: BookStatus) => {
    try {
      const updated = await updateBook({ uuid: book.uuid, status, old_status: book.status });
      setBooks((prev) => prev.map((item) => (item.uuid === book.uuid ? updated : item)));
    } catch (err) {
      setError('Unable to update status.');
    }
  };

  const handleEditBook = async (book: Book, title: string, author: string | undefined, item_type?: string) => {
    try {
      const updated = await updateBook({ uuid: book.uuid, title, author, item_type });
      setBooks((prev) => prev.map((item) => (item.uuid === book.uuid ? updated : item)));
    } catch (err) {
      setError('Unable to update book.');
    }
  };

  const handleReorder = async (reorderedBooks: Book[]) => {
    const updatedQueue = reorderedBooks.map((item, position) => ({
      ...item,
      queue_position: position + 1,
    }));

    // Capture previous state for rollback
    const previousBooks = books;

    // Optimistically update the UI immediately
    setBooks((prev) => {
      const other = prev.filter((item) => item.status !== 'in queue');
      return [...other, ...updatedQueue];
    });

    try {
      await updateQueue(reorderedBooks.map((item) => item.uuid));
    } catch (err) {
      // Revert to previous state on failure
      setBooks(previousBooks);
      setError('Unable to reorder queue. Your changes have been reverted.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    clearAutoRefresh();
    navigate('/login', { replace: true });
  };

  const handleExportMarkdown = useCallback(async () => {
    const inProgress = readingBooks.map(formatBookMarkdown);
    const toRead = queueBooks.map(formatBookMarkdown);
    const lines = ['#### In progress', ''];

    if (inProgress.length) {
      lines.push(...inProgress.map((entry) => `- ${entry}`));
    }

    lines.push('', '#### To read', '');

    if (toRead.length) {
      lines.push(...toRead.map((entry) => `1. ${entry}`));
    }

    const markdown = lines.join('\n');
    try {
      await navigator.clipboard.writeText(markdown);
      setMarkdownCopied(true);
      window.setTimeout(() => setMarkdownCopied(false), 2000);
    } catch (err) {
      setError('Unable to copy markdown.');
    }
  }, [queueBooks, readingBooks]);

  const HomeView = () => {
    const [showFinished, setShowFinished] = useState(false);
    const [showAbandoned, setShowAbandoned] = useState(false);
    const [showProbablyNot, setShowProbablyNot] = useState(false);
    const [typeFilter, setTypeFilter] = useState<string>('all');

    const allItemTypes = useMemo(() => {
      const types = new Set(books.map((b) => b.item_type || 'book'));
      return Array.from(types).sort();
    }, [books]);

    const applyTypeFilter = useCallback(
      (list: Book[]) =>
        typeFilter === 'all' ? list : list.filter((b) => (b.item_type || 'book') === typeFilter),
      [typeFilter]
    );

    const filteredQueue = useMemo(() => applyTypeFilter(queueBooks), [applyTypeFilter, queueBooks]);
    const filteredReading = useMemo(() => applyTypeFilter(readingBooks), [applyTypeFilter, readingBooks]);
    const filteredOccasionalReference = useMemo(() => applyTypeFilter(occasionalReferenceBooks), [applyTypeFilter, occasionalReferenceBooks]);
    const filteredFinished = useMemo(() => applyTypeFilter(finishedBooks), [applyTypeFilter, finishedBooks]);
    const filteredAbandoned = useMemo(() => applyTypeFilter(abandonedBooks), [applyTypeFilter, abandonedBooks]);
    const filteredProbablyNot = useMemo(() => applyTypeFilter(probablyNotBooks), [applyTypeFilter, probablyNotBooks]);

    return (
      <>
        {error && <div className="alert error">{error}</div>}

        <div className="card" style={{ marginBottom: '2rem' }}>
          <div className="card-section">
            <h2>Add</h2>
            <p className="helper-text">Log something you've read or want to read.</p>
            <BookForm books={books} onCreate={handleCreate} />
          </div>
        </div>

        {allItemTypes.length > 1 && (
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="card-section" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="field-label" style={{ marginRight: '0.25rem' }}>Filter by type:</span>
              <button
                className={`button small ${typeFilter === 'all' ? '' : 'ghost'}`}
                onClick={() => setTypeFilter('all')}
              >
                All
              </button>
              {allItemTypes.map((t) => (
                <button
                  key={t}
                  className={`button small ${typeFilter === t ? '' : 'ghost'}`}
                  onClick={() => setTypeFilter(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-section">
          <h2>Reading</h2>
          <p className="helper-text">Keep tabs on what you are actively reading.</p>
          <BookList
            books={filteredReading}
            emptyMessage="Nothing marked as reading yet."
            onSelect={(book) => navigate(`/${book.uuid}`)}
            onStatusChange={handleStatusChange}
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-section">
          <h2>Occasional reference</h2>
          <p className="helper-text">Useful items you return to from time to time.</p>
          <BookList
            books={filteredOccasionalReference}
            emptyMessage="Nothing marked for occasional reference yet."
            onSelect={(book) => navigate(`/${book.uuid}`)}
            onStatusChange={handleStatusChange}
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-section">
          <h2>Your queue</h2>
          <p className="helper-text">Drag items to reorder.</p>
          <BookList
            books={filteredQueue}
            emptyMessage="Your queue is empty. Add something new."
            onSelect={(book) => navigate(`/${book.uuid}`)}
            onStatusChange={handleStatusChange}
            onReorder={typeFilter === 'all' ? handleReorder : undefined}
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-section">
          <div className="section-header">
            <div>
              <h2>Probably not</h2>
              <p className="helper-text">Parked — not now, probably not soon.</p>
            </div>
            <button
              className="button ghost small"
              onClick={() => setShowProbablyNot((prev) => !prev)}
            >
              {showProbablyNot ? 'Hide probably not' : `Show probably not (${filteredProbablyNot.length})`}
            </button>
          </div>
          {showProbablyNot && (
            <BookList
              books={filteredProbablyNot}
              emptyMessage="Nothing parked here."
              onSelect={(book) => navigate(`/${book.uuid}`)}
              onStatusChange={handleStatusChange}
            />
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-section">
          <div className="section-header">
            <div>
              <h2>Finished</h2>
              <p className="helper-text">Completed items are tucked away here.</p>
            </div>
            <button
              className="button ghost small"
              onClick={() => setShowFinished((prev) => !prev)}
            >
              {showFinished ? 'Hide finished' : `Show finished (${filteredFinished.length})`}
            </button>
          </div>
          {showFinished && (
            <BookList
              books={filteredFinished}
              emptyMessage="Nothing marked as finished yet."
              onSelect={(book) => navigate(`/${book.uuid}`)}
              onStatusChange={handleStatusChange}
            />
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="card-section">
          <div className="section-header">
            <div>
              <h2>Abandoned</h2>
              <p className="helper-text">Items you stopped reading.</p>
            </div>
            <button
              className="button ghost small"
              onClick={() => setShowAbandoned((prev) => !prev)}
            >
              {showAbandoned ? 'Hide abandoned' : `Show abandoned (${filteredAbandoned.length})`}
            </button>
          </div>
          {showAbandoned && (
            <BookList
              books={filteredAbandoned}
              emptyMessage="No abandoned items."
              onSelect={(book) => navigate(`/${book.uuid}`)}
              onStatusChange={handleStatusChange}
            />
          )}
        </div>
      </div>

      </>
    );
  };

  const BookDetailView = () => {
    const { bookId } = useParams();
    const book = getBookById(bookId);
    const [editing, setEditing] = useState(false);
    const [draftTitle, setDraftTitle] = useState('');
    const [draftAuthor, setDraftAuthor] = useState('');
    const [draftItemType, setDraftItemType] = useState('book');
    const [draftCustomType, setDraftCustomType] = useState('');
    const [saving, setSaving] = useState(false);
    const [convertingToSection, setConvertingToSection] = useState(false);
    const [selectedParentUuid, setSelectedParentUuid] = useState('');
    const [convertSectionDescription, setConvertSectionDescription] = useState('');
    const [convertSaving, setConvertSaving] = useState(false);

    if (!book) {
      return (
        <div className="card">
          <div className="card-section">
            <h2>Book not found</h2>
            <p className="helper-text">We could not find that book in your list.</p>
            <button className="button secondary" onClick={() => navigate('/home')}>
              Back to home
            </button>
          </div>
        </div>
      );
    }

    const startEditing = () => {
      setDraftTitle(book.title);
      setDraftAuthor(book.author ?? '');
      const currentType = book.item_type || 'book';
      const isStandard = STANDARD_ITEM_TYPES.includes(currentType as any);
      setDraftItemType(isStandard ? currentType : '__custom');
      setDraftCustomType(isStandard ? '' : currentType);
      setEditing(true);
    };

    const cancelEditing = () => {
      setEditing(false);
    };

    const saveEdits = async () => {
      const trimmedTitle = draftTitle.trim();
      if (!trimmedTitle) return;
      const resolvedType = draftItemType === '__custom' ? draftCustomType.trim().toLowerCase() : draftItemType;
      if (!resolvedType) return;
      setSaving(true);
      await handleEditBook(book, trimmedTitle, draftAuthor.trim() || undefined, resolvedType);
      setSaving(false);
      setEditing(false);
    };

    const handleConvertToSection = async () => {
      if (!selectedParentUuid) return;
      setConvertSaving(true);
      try {
        const updated = await updateBook({
          uuid: book.uuid,
          is_section: true,
          parent_uuid: selectedParentUuid,
          section_description: convertSectionDescription.trim() || undefined,
        });
        setBooks((prev) => prev.map((item) => (item.uuid === book.uuid ? updated : item)));
        setConvertingToSection(false);
        setSelectedParentUuid('');
        setConvertSectionDescription('');
      } catch {
        setError('Unable to convert to section.');
      } finally {
        setConvertSaving(false);
      }
    };

    const handleConvertToFullWork = async () => {
      setConvertSaving(true);
      try {
        const updated = await updateBook({ uuid: book.uuid, is_section: false, parent_uuid: '' });
        setBooks((prev) => prev.map((item) => (item.uuid === book.uuid ? updated : item)));
      } catch {
        setError('Unable to convert to full work.');
      } finally {
        setConvertSaving(false);
      }
    };

    const parentBook = book.parent_uuid ? getBookById(book.parent_uuid) : undefined;
    const eligibleParents = books.filter((b) => b.uuid !== book.uuid && !b.is_section);

    const guessParentUuid = (): string => {
      if (!eligibleParents.length) return '';
      // Prefer a "reading" work by the same author
      if (book.author) {
        const sameAuthorReading = eligibleParents.find(
          (b) => b.status === 'reading' && b.author === book.author
        );
        if (sameAuthorReading) return sameAuthorReading.uuid;
      }
      // Then any "reading" work
      const reading = eligibleParents.find((b) => b.status === 'reading');
      if (reading) return reading.uuid;
      // Fallback to first eligible parent
      return eligibleParents[0].uuid;
    };

    return (
      <div>
        <div className="card">
          <div className="card-section" style={{ display: 'grid', gap: '1rem' }}>
            {editing ? (
              <div className="book-edit-form">
                <div className="field">
                  <label className="field-label" htmlFor="edit-title">Title</label>
                  <input
                    id="edit-title"
                    className="input"
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdits();
                      if (e.key === 'Escape') cancelEditing();
                    }}
                    autoFocus
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="edit-author">Author</label>
                  <input
                    id="edit-author"
                    className="input"
                    value={draftAuthor}
                    placeholder="Optional"
                    onChange={(e) => setDraftAuthor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdits();
                      if (e.key === 'Escape') cancelEditing();
                    }}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="edit-item-type">Type</label>
                  <select
                    id="edit-item-type"
                    className="select"
                    value={draftItemType}
                    onChange={(e) => {
                      setDraftItemType(e.target.value);
                      if (e.target.value !== '__custom') {
                        setDraftCustomType('');
                      }
                    }}
                  >
                    {STANDARD_ITEM_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                    <option value="__custom">Other…</option>
                  </select>
                </div>
                {draftItemType === '__custom' && (
                  <div className="field">
                    <label className="field-label" htmlFor="edit-custom-type">Custom type</label>
                    <input
                      id="edit-custom-type"
                      className="input"
                      value={draftCustomType}
                      onChange={(e) => setDraftCustomType(e.target.value)}
                      placeholder="e.g. graphic novel, libretto"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdits();
                        if (e.key === 'Escape') cancelEditing();
                      }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="button small" onClick={saveEdits} disabled={saving || !draftTitle.trim() || (draftItemType === '__custom' && !draftCustomType.trim())}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button className="button ghost small" onClick={cancelEditing} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h2>{book.title}</h2>
                <p className="helper-text">{book.author ? `by ${book.author}` : 'Author unknown'}</p>
                {book.is_section && parentBook && (
                  <p className="helper-text">
                    {book.section_description ? `${book.section_description} — section of ` : 'Section of: '}
                    <a
                      href={`/${parentBook.uuid}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/${parentBook.uuid}`); }}
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      {parentBook.title}
                    </a>
                  </p>
                )}
                {book.is_section && !parentBook && (
                  <p className="helper-text">Section (parent work not found)</p>
                )}
                <p className="helper-text">
                  {(book.item_type && book.item_type !== 'book')
                    ? `${book.item_type.charAt(0).toUpperCase() + book.item_type.slice(1)} \u00B7 `
                    : ''
                  }Status: {book.status}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="button secondary" onClick={() => navigate('/home')}>
                Back to home
              </button>
              {!editing && (
                <button className="button ghost small" onClick={startEditing}>
                  Edit
                </button>
              )}
              {!editing && !convertingToSection && book.is_section && (
                <button
                  className="button ghost small"
                  onClick={handleConvertToFullWork}
                  disabled={convertSaving}
                >
                  {convertSaving ? 'Converting…' : 'Convert to full work'}
                </button>
              )}
              {!editing && !convertingToSection && !book.is_section && (
                <button
                  className="button ghost small"
                  onClick={() => { setSelectedParentUuid(guessParentUuid()); setConvertingToSection(true); }}
                >
                  Convert to section
                </button>
              )}
            </div>
            {convertingToSection && (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <label className="field-label" htmlFor="parent-select">Parent work</label>
                <select
                  id="parent-select"
                  className="input"
                  value={selectedParentUuid}
                  onChange={(e) => setSelectedParentUuid(e.target.value)}
                >
                  <option value="">Select a work…</option>
                  {eligibleParents.map((b) => (
                    <option key={b.uuid} value={b.uuid}>
                      {b.title}{b.author ? ` — ${b.author}` : ''}
                    </option>
                  ))}
                </select>
                <label className="field-label" htmlFor="section-desc">Section description</label>
                <input
                  id="section-desc"
                  className="input"
                  value={convertSectionDescription}
                  onChange={(e) => setConvertSectionDescription(e.target.value)}
                  placeholder='e.g. "Preface", "Chapters 1-3"'
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="button small"
                    onClick={handleConvertToSection}
                    disabled={convertSaving || !selectedParentUuid}
                  >
                    {convertSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="button ghost small"
                    onClick={() => { setConvertingToSection(false); setSelectedParentUuid(''); setConvertSectionDescription(''); }}
                    disabled={convertSaving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <BookNotesPreview book={book} onEdit={() => navigate(`/notes/${book.uuid}`)} />
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="card-section">
            <h3>History</h3>
            <p className="helper-text" style={{ marginBottom: '0.75rem' }}>
              Track when this book was added and status changes.
            </p>
            <BookHistory book={book} />
          </div>
        </div>
      </div>
    );
  };

  const NotesView = () => {
    const { bookId } = useParams();
    const book = getBookById(bookId);
    if (!book) {
      return (
        <div className="card">
          <div className="card-section">
            <h2>Book not found</h2>
            <p className="helper-text">We could not find notes for that book.</p>
            <button className="button secondary" onClick={() => navigate('/home')}>
              Back to home
            </button>
          </div>
        </div>
      );
    }

    return <BookNotes book={book} onBack={() => navigate(`/${book.uuid}`)} />;
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__left">
          <div>
            <div className="app-title">Reading Queue</div>
            <div className="helper-text">Track what you want to read and what you are reading.</div>
          </div>
          <div className="nav-tabs">
            <button
              className={`nav-tab${location.pathname === '/home' || location.pathname === '/' ? ' active' : ''}`}
              onClick={() => navigate('/home')}
            >
              Main
            </button>
            <button
              className={`nav-tab${location.pathname.startsWith('/history') ? ' active' : ''}`}
              onClick={() => navigate('/history')}
            >
              History
            </button>
          </div>
        </div>
        <div className="app-header__actions">
          <button className="button secondary" onClick={handleExportMarkdown} type="button">
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: '0.4rem' }}
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {markdownCopied ? 'Copied' : 'Copy Markdown'}
          </button>
          <button className="button secondary" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </header>

      <main className="app-content">
        {loading ? (
          <div className="card">
            <div className="card-section">Loading your books...</div>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<HomeView />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/notes/:bookId" element={<NotesView />} />
            <Route path="/:bookId" element={<BookDetailView />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
};

const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = Boolean(localStorage.getItem('access_token'));
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const App: React.FC = () => {
  const isAuthenticated = Boolean(localStorage.getItem('access_token'));

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/home" replace /> : <Login />} />
      <Route path="/signup" element={isAuthenticated ? <Navigate to="/home" replace /> : <Signup />} />
      <Route path="/password-reset" element={<PasswordReset />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/*" element={(
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      )}
      />
    </Routes>
  );
};

export default App;
